'use client';

import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import {
  ButtonSecondarySize,
  DropdownOrientation,
  FsButtonSecondary,
  FsDropdown,
  FsHeading,
  FsImage,
  FsListContent,
  FsPopoverMenu,
  FsPopoverMenuItem,
  FsTextContent,
  FsTopSection,
  HeadingSize,
  HeadingTag,
  ListContentOrientation,
  ListContentType,
  ListContentVariant,
  ModalType,
  PopoverMenuItemType,
  TopSectionType,
} from '@printi/ds-offset-react-core';
import axios from 'axios';
import Image from 'next/image';
import { Rate } from './Rate.styles';
import * as S from './TrustvoxReviews.styles';
import { TrustvoxEmpty } from './TrustvoxEmpty';
import { ReviewGallery } from './ReviewGallery';
import { StyledModal } from './ReviewGallery.styles';

type productDataTrustvoxProps = {
  _productId?: string;
  _productName?: string;
  _productPhotos?: string[];
  starsCount: starsCountProps;
};

interface starsCountProps {
  histogram: number[] | object;
  average: number;
  count: number;
}

export interface reviewProps {
  id: number;
  rate: number;
  comments: commentsProps[];
  opinion_title?: string;
  opinion: string;
  created_at: string;
  review_photos: reviewPhotosProps[];
}

interface reviewPhotosProps {
  id: number;
  thumbnail: string;
  image: string;
}

interface commentsProps {
  text?: string;
}

export const HistogramRate = (productData: productDataTrustvoxProps) => {
  const [histogram, setHistogram] = useState<number[]>([]);
  const [reviews, setReviews] = useState([]);
  const [orderReviews, setOrderReviews] = useState<'created_at' | 'rate'>(
    'rate',
  );
  const [itemsPerPage, setItemsPerPage] = useState<number>(8);
  const [photoModal, setPhotoModal] = useState<Partial<reviewProps>>({});
  const [showModal, setShowModal] = useState(false);
  const [hasError, setHasError] = useState(false);

  const FsProgressLine = dynamic(
    () =>
      import('@printi/ds-offset-react-core').then(
        (module) => module.FsProgressLine,
      ),
    { ssr: false },
  );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (productData?.starsCount?.histogram) {
      const arr = Array.from(
        Object.entries(productData?.starsCount?.histogram),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        ([key, value]) => value,
      );
      setHistogram(() => arr);
    }
  }, [productData?.starsCount]);

  const STORE_ID = String(process.env.TRUSTVOX_STORE_ID);
  const TOKEN = String(process.env.TRUSTVOX_TOKEN);
  const OPINIONS_URL = String(process.env.TRUSTVOX_OPINIONS_URL);
  const API_URL = String(OPINIONS_URL).concat(
    '?',
    new URLSearchParams({
      code: productData?._productId || '',
      store_id: STORE_ID,
      not_empty: 'false',
      page: '1',
      per: itemsPerPage?.toString(),
      order_by: `-${orderReviews}`,
    }).toString(),
  );

  const fetchReviews = async () => {
    await axios
      .get(API_URL, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/vnd.trustvox-v2+json',
          Authorization: `token ${TOKEN}`,
        },
      })
      .then(function (response) {
        setReviews(response.data.items);
      })
      .catch(function (error) {
        // eslint-disable-next-line
        console.log(error);
        setHasError(true);
      });
  };

  useEffect(() => {
    fetchReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsPerPage]);

  if (productData?.starsCount?.count === 0 || hasError) {
    return <TrustvoxEmpty />;
  }

  return (
    <S.TrustvoxContainer id="avaliacoes">
      <S.RateContainer>
        <div className="sticky">
          <FsHeading tag={HeadingTag.SPAN} size={HeadingSize.XL}>
            {productData.starsCount.average}
          </FsHeading>
          <FsTextContent
            heading={''}
            paragraph={''}
            showRateStatus
            rating={productData.starsCount.average}
            ratingDescription={`${productData.starsCount.count} avaliações`}
          />
          <Rate>
            {histogram.length !== 0 && productData.starsCount.count !== 0 && (
              <>
                <div>
                  <span>5</span>
                  <FsProgressLine
                    value={(histogram[4] / productData.starsCount.count) * 100}
                  />
                </div>
                <div>
                  <span>4</span>
                  <FsProgressLine
                    value={(histogram[3] / productData.starsCount.count) * 100}
                  />
                </div>
                <div>
                  <span>3</span>
                  <FsProgressLine
                    value={(histogram[2] / productData.starsCount.count) * 100}
                  />
                </div>
                <div>
                  <span>2</span>
                  <FsProgressLine
                    value={(histogram[1] / productData.starsCount.count) * 100}
                  />
                </div>
                <div>
                  <span>1</span>
                  <FsProgressLine
                    value={(histogram[0] / productData.starsCount.count) * 100}
                  />
                </div>
              </>
            )}
          </Rate>
          <div className="ts-v-footer-container">
            Avaliações reais, auditadas por
            <a
              href="https://site.trustvox.com.br/conheca-a-trustvox"
              target="_blank"
            >
              <Image
                title="Reclame Aqui"
                alt="Reclame Aqui"
                src="data:image/png;base64,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"
                width={94}
                height={17}
              />
            </a>
          </div>
        </div>
      </S.RateContainer>
      <div>
        <FsTopSection
          heading="Avaliações"
          headingProps={{ tag: HeadingTag.H2 }}
          showDescription={false}
          type={TopSectionType.Dropdown}
          actionSlot={
            <FsDropdown
              description="Ordenar por:"
              label={
                orderReviews === 'created_at' ? 'Mais recentes' : 'Mais úteis'
              }
              orientation={DropdownOrientation.Horizontal}
            >
              <FsPopoverMenu style={{ right: '0' }}>
                <FsPopoverMenuItem
                  label="Mais recentes"
                  onSelected={() => {
                    setOrderReviews('created_at');
                    setItemsPerPage(8);
                  }}
                  type={PopoverMenuItemType.Text}
                />
                <FsPopoverMenuItem
                  label="Mais úteis"
                  onSelected={() => {
                    setOrderReviews('rate');
                    setItemsPerPage(8);
                  }}
                  type={PopoverMenuItemType.Text}
                />
              </FsPopoverMenu>
            </FsDropdown>
          }
        />
        {(reviews || []).map((item: reviewProps) => (
          <S.Review key={item.id}>
            <FsTextContent
              heading={''}
              paragraph={''}
              showRateStatus
              rating={item.rate}
            />
            <FsListContent
              type={ListContentType.Single}
              variant={ListContentVariant.Line}
              orientation={ListContentOrientation.Horizontal}
              showButton={false}
              showTag={false}
              showHeading={true}
              heading={
                !item.opinion_title && !item.opinion
                  ? 'Não foi deixada nenhuma opinião, apenas nota'
                  : item.opinion_title || ''
              }
              description=""
              showLabelLeft={true}
              labelLeft={item.opinion || ''}
              showContentLeft={true}
              showContentRight={true}
              labelRight={item.created_at}
              lastItem={true}
            />
            <div className="image-list">
              {(item.review_photos || []).map((photo, index) => {
                if (index < 2) {
                  return (
                    <FsImage
                      aspectRatio={'1:1'}
                      contentSlot={
                        <Image
                          src={photo.thumbnail}
                          alt={`${item.opinion_title} - foto ${photo.id}`}
                          width={80}
                          height={80}
                        />
                      }
                      key={photo.id}
                      onClick={() => {
                        setPhotoModal({ ...item });
                        setShowModal(true);
                      }}
                    />
                  );
                }
              })}
              {item.review_photos.length > 2 && (
                <div
                  className="more-images"
                  onClick={() => {
                    setPhotoModal({ ...item });
                    setShowModal(true);
                  }}
                >
                  +{item.review_photos.length}
                </div>
              )}
            </div>
            {item.comments.length > 0 && (
              <FsListContent
                type={ListContentType.Single}
                variant={ListContentVariant.Line}
                orientation={ListContentOrientation.Horizontal}
                className="printi-answer"
                showButton={false}
                showTag={false}
                showHeading={true}
                heading={'Resposta da Printi'}
                description=""
                showLabelLeft={true}
                labelLeft={item.comments[0]?.text || ''}
                showContentLeft={true}
                showContentRight={false}
                lastItem={true}
              />
            )}
          </S.Review>
        ))}
        <FsButtonSecondary
          size={ButtonSecondarySize.SM}
          onClick={() => {
            setItemsPerPage(itemsPerPage + 8);
            window.dataLayer.push({
              event: 'genericEvent',
              event_name: 'reviews_load_more',
            });
          }}
          disabled={productData.starsCount.count <= itemsPerPage ? true : false}
          className="loadMore"
        >
          Carregar mais
        </FsButtonSecondary>
      </div>
      <StyledModal
        isOpen={showModal}
        type={ModalType.Slot}
        heading="Avaliação com foto"
        headingProps={{
          tag: HeadingTag.SPAN,
          size: HeadingSize.SM,
        }}
        onClose={() => setShowModal(false)}
      >
        <ReviewGallery item={photoModal} />
      </StyledModal>
    </S.TrustvoxContainer>
  );
};
